
import React, { useState } from 'react';
import { Merchant, MealType } from '../types';

interface DashboardProps {
  merchants: Merchant[];
  onMerchantClick: (id: string) => void;
  onStartSampling: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ merchants, onMerchantClick, onStartSampling }) => {
  const [selectedDay, setSelectedDay] = useState(0);

  // 动态获取最近3天的日期，仅年月日格式
  const getDateStr = (offset: number) => {
    const d = new Date();
    d.setDate(d.getDate() - offset);
    return d.toISOString().split('T')[0];
  };

  const dateTabs = [
    { date: getDateStr(0) },
    { date: getDateStr(1) },
    { date: getDateStr(2) },
  ];

  return (
    <div className="flex flex-col h-full space-y-4 overflow-hidden">
      {/* 日期导航 - 仅3天日期显示，移除周几描述 */}
      <div className="flex space-x-2 bg-slate-200 p-1 rounded-2xl w-fit shadow-inner">
        {dateTabs.map((tab, idx) => (
          <button 
            key={tab.date}
            onClick={() => setSelectedDay(idx)}
            className={`px-12 py-3 rounded-xl font-black transition-all min-w-[150px] ${
              selectedDay === idx 
              ? 'bg-white text-blue-700 shadow-md ring-1 ring-blue-50 transform scale-105' 
              : 'text-slate-500 active:bg-slate-300'
            }`}
          >
            <span className="text-xl font-mono tracking-tighter">{tab.date}</span>
          </button>
        ))}
      </div>

      {/* 商户列表网格 - 3x2 布局（对应App.tsx中的6个商户） */}
      <div className="grid grid-cols-3 gap-4 flex-1 overflow-y-auto pb-32 no-scrollbar">
        {merchants.map((m) => (
          <div 
            key={m.id} 
            onClick={() => onMerchantClick(m.id)}
            className="bg-white rounded-3xl shadow-sm border border-slate-200/60 p-5 flex flex-col justify-between active:bg-blue-50 active:scale-[0.98] transition-all"
          >
            <div className="text-center mb-3">
              <h3 className="text-2xl font-black text-slate-800 tracking-tight uppercase">{m.name}</h3>
              <div className="w-12 h-1 bg-blue-500 mx-auto mt-2 rounded-full opacity-20"></div>
            </div>
            
            <div className="grid grid-cols-2 gap-2 mb-4">
              {Object.entries(m.counts).map(([type, count]) => (
                <div key={type} className="flex flex-col items-center p-2 rounded-2xl bg-slate-50 border border-slate-100">
                  <span className="text-slate-400 text-[10px] font-black">{type}</span>
                  <span className="font-mono text-2xl font-black text-blue-600 leading-tight">{count}</span>
                </div>
              ))}
            </div>

            {/* 操作提示常驻显示，适配Android移动端交互 */}
            <div className="flex items-center justify-center space-x-2 bg-blue-600 text-white py-3 rounded-2xl shadow-lg shadow-blue-100">
              <span className="text-sm font-black tracking-widest uppercase">进入留样台账</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        ))}
      </div>

      {/* 底部固定操作栏 - 针对Android触屏优化点击区域 */}
      <div className="fixed bottom-12 left-1/2 -translate-x-1/2 flex space-x-8 w-full max-w-4xl px-8 pointer-events-none">
        <button 
          onClick={onStartSampling}
          className="pointer-events-auto flex-1 bg-blue-600 active:bg-blue-800 text-white text-xl font-black py-5 rounded-[2.5rem] shadow-xl shadow-blue-500/30 transition-all flex items-center justify-center space-x-4 border-b-4 border-blue-900"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
          <span>我要留样</span>
        </button>
        <button className="pointer-events-auto flex-1 bg-amber-500 active:bg-amber-700 text-white text-xl font-black py-5 rounded-[2.5rem] shadow-xl shadow-amber-500/20 transition-all flex items-center justify-center space-x-4 border-b-4 border-amber-700">
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
          <span>取样管理</span>
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
